/** Configuração global de elementos do BI (Ctrl+H) — S2 `/sistema/bi-ui-visibility`. */
import { resetSessaoFinanceira } from './bridge'
import type { BiUiVisibility, ToggleableNavId } from '../state/BiUiVisibilityContext'

const API_BASE = (import.meta.env.VITE_FINANCIAL_API_BASE as string) || '/api/v1'

const NAV_IDS: ToggleableNavId[] = [
  'margens',
  'operacoes',
  'pedidos',
  'fiscal',
  'agenda',
  'pagamentos',
  'fretes',
  'despesas',
  'clientes',
]

function apiRoot() {
  return API_BASE.replace(/\/$/, '')
}

export type BiUiVisibilityApiData = {
  prefs: BiUiVisibility
  source: 'mongo' | 'default'
  canEdit: boolean
}

function normalizePrefs(raw: Partial<BiUiVisibility> | undefined): BiUiVisibility {
  const nav = {
    margens: false,
    operacoes: false,
    pedidos: false,
    fiscal: false,
    agenda: false,
    pagamentos: false,
    fretes: false,
    despesas: false,
    clientes: false,
  } as Record<ToggleableNavId, boolean>
  if (raw?.nav && typeof raw.nav === 'object') {
    for (const key of NAV_IDS) {
      if (typeof raw.nav[key] === 'boolean') nav[key] = raw.nav[key]
    }
  }
  return {
    fonteDeDados: typeof raw?.fonteDeDados === 'boolean' ? raw.fonteDeDados : true,
    proveniencia: typeof raw?.proveniencia === 'boolean' ? raw.proveniencia : true,
    nav,
  }
}

export async function fetchBiUiVisibility(): Promise<BiUiVisibilityApiData> {
  const res = await fetch(`${apiRoot()}/sistema/bi-ui-visibility`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store',
  })
  if (res.status === 401) {
    resetSessaoFinanceira()
    throw new Error('Sessão inválida')
  }
  if (!res.ok) throw new Error(`bi-ui-visibility: HTTP ${res.status}`)
  const json = (await res.json()) as {
    success?: boolean
    data?: { prefs?: Partial<BiUiVisibility>; source?: string; canEdit?: boolean }
  }
  return {
    prefs: normalizePrefs(json.data?.prefs),
    source: json.data?.source === 'mongo' ? 'mongo' : 'default',
    canEdit: Boolean(json.data?.canEdit),
  }
}

export async function saveBiUiVisibility(prefs: BiUiVisibility): Promise<BiUiVisibilityApiData> {
  const res = await fetch(`${apiRoot()}/sistema/bi-ui-visibility`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(prefs),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    data?: { prefs?: Partial<BiUiVisibility>; source?: string; canEdit?: boolean }
    error?: { message?: string }
  }
  if (res.status === 401) {
    resetSessaoFinanceira()
    throw new Error('Sessão inválida')
  }
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message || `Falha ao guardar (HTTP ${res.status})`)
  }
  return {
    prefs: normalizePrefs(json.data?.prefs),
    source: 'mongo',
    canEdit: json.data?.canEdit !== false,
  }
}
